import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, HostListener, Output, Input, ViewEncapsulation, Component, Injectable, ViewChild, NgModule } from '@angular/core';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i2 from '@engineering11/ui-lib/e11-closer';
import { E11CloserModule } from '@engineering11/ui-lib/e11-closer';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';
import { E11TemplateMap, IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { animationFadeInOut, E11Animations } from '@engineering11/ui-lib/e11-animations';

// Necessary to allow for both modal & global modal with default config
const defaultModalConfig = {
    title: '',
    width: 'md',
    bodyHeight: 'auto',
    titleAlign: '',
    hideCloseButton: false,
    closer: false,
    backdropStyle: 'dark',
    closeOnClickOutside: false,
    closeOnEscape: true,
    darkMode: false,
    hasBackdrop: true,
    showFooter: false,
    showHeader: true,
    headerCTAs: [],
};
const Share = 'Share';
const View_Details = 'View Details';

var ModalTemplateKey;
(function (ModalTemplateKey) {
    ModalTemplateKey["Header"] = "Header";
    ModalTemplateKey["Body"] = "Body";
    ModalTemplateKey["Footer"] = "Footer";
})(ModalTemplateKey || (ModalTemplateKey = {}));
/**
 * A customizable modal component that allows for lazy loading of content into the modal.
 *
 * Use `<ng-template e11Template="Body">` to pass the body into the modal - this means the content of the template is only loaded/injected into the DOM lazily, as needed.
 * If you NEED the content of the modal to be loaded eagerly, anywhere the modal exists, use `<ng-container modal-body>`
 *
 * The modal includes several configuration options such as width, height, title alignment,
 * and the ability to close on outside click or escape key press.
 *
 * @example
 * <e11-modal [title]="'Modal Title'" [width]="'medium'"></e11-modal>
 */
class E11ModalComponent extends E11TemplateMap {
    constructor(elementRef) {
        super();
        this.elementRef = elementRef;
        /**
         * The title of the modal.
         * @type {string}
         * @default ''
         */
        this.title = defaultModalConfig.title;
        /**
         * The width of the modal.
         * @type {ModalWidth}
         * @default 'md'
         */
        this.width = defaultModalConfig.width;
        /**
         * The height of the modal body.
         * @type {ModalSizeBodyHeight}
         * @default 'auto'
         */
        this.bodyHeight = defaultModalConfig.bodyHeight;
        /**
         * The alignment of the modal title.
         * @type {TitleAlign}
         * @default ''
         */
        this.titleAlign = defaultModalConfig.titleAlign;
        /**
         * Whether to hide the close button in the modal.
         * @type {boolean}
         * @default false
         */
        this.hideCloseButton = defaultModalConfig.hideCloseButton;
        /**
         * Whether to show the external close button.
         * @type {boolean}
         * @default false
         */
        this.closer = defaultModalConfig.closer; // external close button, like for a lightbox
        /**
         * The class names to apply to the body element.
         * @deprecated This property is deprecated. Please use {@link bodyClass} instead.
         * @type {string}
         * @default ''
         */
        this.bodyClassOverrides = '';
        /**
         * The class names to apply to the body element.
         * @type {string}
         * @default ''
         */
        this.bodyClass = '';
        /**
         * The class names to apply to the modal element.
         * @deprecated This property is deprecated. Please use {@link modalClass} instead.
         * @type {string}
         * @default ''
         */
        this.modalClassOverrides = '';
        /**
         * The class names to apply to the modal element.
         * @type {string}
         * @default ''
         */
        this.modalClass = '';
        /**
         * The class names to apply to the content element.
         * @deprecated This property is deprecated. Please use {@link contentClass} instead.
         * @type {string}
         * @default ''
         */
        this.contentClassOverrides = '';
        /**
         * The class names to apply to the content element.
         * @type {string}
         * @default ''
         */
        this.contentClass = '';
        /**
         * The class names to apply to the header element.
         * @deprecated This property is deprecated. Please use {@link headerClass} instead.
         * @type {string}
         * @default ''
         */
        this.headerClassOverrides = '';
        /**
         * The class names to apply to the header element.
         * @type {string}
         * @default ''
         */
        this.headerClass = '';
        /**
         * The class names to apply to the footer element.
         * @deprecated This property is deprecated. Please use {@link footerClass} instead.
         * @type {string}
         * @default ''
         */
        this.footerClassOverrides = '';
        /**
         * The class names to apply to the footer element.
         * @type {string}
         * @default ''
         */
        this.footerClass = '';
        /**
         * Whether to show the footer in the modal.
         * @type {boolean}
         * @default false
         */
        this.showFooter = defaultModalConfig.showFooter;
        /**
         * Whether to show the header in the modal.
         * @type {boolean}
         * @default true
         */
        this.showHeader = defaultModalConfig.showHeader;
        /**
         * Whether to use the dark mode for the modal.
         * @type {boolean}
         * @default false
         */
        this.darkMode = defaultModalConfig.darkMode;
        /**
         * Whether to close the modal when the escape key is pressed.
         * @type {boolean}
         * @default true
         */
        this.closeOnEscape = defaultModalConfig.closeOnEscape;
        /**
         * Whether to close the modal when clicking outside the modal.
         * @type {boolean}
         * @default false
         */
        this.closeOnClickOutside = defaultModalConfig.closeOnClickOutside;
        /**
         * The call-to-action buttons to display in the modal header.
         * @type {string[]}
         * @default []
         */
        this.headerCTAs = defaultModalConfig.headerCTAs ?? [];
        /**
         * Whether to show the backdrop behind the modal.
         * @type {boolean}
         * @default true
         */
        this.hasBackdrop = defaultModalConfig.hasBackdrop;
        /**
         * The style of the backdrop behind the modal.
         * @type {Mode}
         * @default 'dark'
         */
        this.backdropStyle = defaultModalConfig.backdropStyle;
        /**
         * Whether the backdrop is open.
         * @type {boolean}
         * @default false
         */
        this.isBackdropOpen = false;
        /**
         * Emits event when the modal is opened.
         * @deprecated This event is deprecated. Please use {@link onModalOpened} instead.
         * @type {EventEmitter<any>}
         */
        this.modalOpened = new EventEmitter();
        /**
         * Emits event when the modal is opened.
         * @type {EventEmitter<any>}
         */
        this.onModalOpened = new EventEmitter();
        /**
         * Emits event when the modal is closed.
         * @deprecated This event is deprecated. Please use {@link onModalClosed} instead.
         * @type {EventEmitter<any>}
         */
        this.modalClosed = new EventEmitter();
        /**
         * Emits event when the modal is closed.
         * @type {EventEmitter<any>}
         */
        this.onModalClosed = new EventEmitter();
        /**
         * Emits event when a Call-To-Action (CTA) button in the modal header is clicked.
         * @deprecated This event is deprecated. Please use {@link onHeaderCTAClicked} instead.
         * @type {EventEmitter<string>}
         */
        this.headerCTAClicked = new EventEmitter();
        /**
         * Emits event when a Call-To-Action (CTA) button in the modal header is clicked.
         * @type {EventEmitter<string>}
         */
        this.onHeaderCTAClicked = new EventEmitter();
        /**
         * Whether the modal is open.
         * @type {boolean}
         * @default false
         */
        this.isOpened = false;
        this.ModalTemplateKey = ModalTemplateKey;
        this.isBrowser = inject(IS_BROWSER);
    }
    onKeydownHandler(event) {
        if (this.closeOnEscape && this.isOpened)
            this.close();
    }
    onClick(target) {
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (!clickedInside && this.closeOnClickOutside && this.isOpened)
            this.close();
    }
    ngOnInit() {
        if (!this.isBrowser)
            return null;
        this.body = document.getElementsByTagName('body')[0];
    }
    /**
     * Adds a class to the body element to prevent scrolling when the modal is open.
     */
    addScrollLock() {
        this.body.classList.add('e11-overflow-hidden');
    }
    /**
     * Removes the class from the body element to allow scrolling when the modal is closed.
     * @returns void
     */
    releaseScrollLock() {
        this.body.classList.remove('e11-overflow-hidden');
    }
    /**
     * Opens the modal.
     * @param {any[]} args - The arguments to pass to the modal.
     * @returns void
     */
    open(...args) {
        setTimeout(() => {
            this.isBackdropOpen = true;
            this.isOpened = true;
            // TODO: Remove this in the next major version
            this.modalOpened.emit(args);
            this.onModalOpened.emit(args);
            this.addScrollLock();
        });
    }
    /**
     * Closes the modal.
     * @param {any[]} args - The arguments to pass to the modal.
     * @returns void
     */
    close(...args) {
        this.isOpened = false;
        this.isBackdropOpen = false;
        // TODO: Remove this in the next major version
        this.modalClosed.emit(args);
        this.onModalClosed.emit(args);
        this.releaseScrollLock();
    }
    /**
     * Handles the click event on a Call-To-Action (CTA) button in the modal header.
     * @param {string} cta - The identifier of the CTA button that was clicked.
     * @returns void
     */
    handleHeaderCTAClick(cta) {
        // TODO: Remove this in the next major version
        this.headerCTAClicked.emit(cta);
        this.onHeaderCTAClicked.emit(cta);
    }
    /**
     * Closes the modal when clicking outside the modal.
     * @param {any[]} args - The arguments to pass to the modal.
     * @returns void
     */
    closeFromClickOutside(...args) {
        if (this.closeOnClickOutside && this.isOpened) {
            this.isOpened = false;
            this.isBackdropOpen = false;
            // TODO: Remove this in the next major version
            this.modalClosed.emit(args);
            this.onModalClosed.emit(args);
            this.releaseScrollLock();
        }
    }
    ngOnDestroy() {
        // ? Is it possible for these to run before the contents of open() if the component is opened and destroyed immediately?
        this.isBackdropOpen = false;
        this.releaseScrollLock();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ModalComponent, isStandalone: true, selector: "e11-modal", inputs: { title: "title", width: "width", bodyHeight: "bodyHeight", titleAlign: "titleAlign", hideCloseButton: "hideCloseButton", closer: "closer", bodyClassOverrides: "bodyClassOverrides", bodyClass: "bodyClass", modalClassOverrides: "modalClassOverrides", modalClass: "modalClass", contentClassOverrides: "contentClassOverrides", contentClass: "contentClass", headerClassOverrides: "headerClassOverrides", headerClass: "headerClass", footerClassOverrides: "footerClassOverrides", footerClass: "footerClass", showFooter: "showFooter", showHeader: "showHeader", darkMode: "darkMode", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside", headerCTAs: "headerCTAs", hasBackdrop: "hasBackdrop", backdropStyle: "backdropStyle" }, outputs: { modalOpened: "modalOpened", onModalOpened: "onModalOpened", modalClosed: "modalClosed", onModalClosed: "onModalClosed", headerCTAClicked: "headerCTAClicked", onHeaderCTAClicked: "onHeaderCTAClicked" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)", "document:click": "onClick($event.target)" } }, usesInheritance: true, ngImport: i0, template: "<div\n  *ngIf=\"isOpened\"\n  [@animationScaleSlideUp]\n  [ngClass]=\"{ 'e11-modal-container-active': isOpened, 'e11-overflow-y-scroll': bodyHeight === 'auto' }\"\n  class=\"e11-modal-container\"\n>\n  <div\n    [ngClass]=\"{ 'e11-flex e11-min-h-screen e11-mt-0': width === 'full', 'e11-mt-16': width !== 'full' }\"\n    class=\"e11-modal e11-absolute e11-modal-responsive e11-modal-{{ width }} {{ modalClassOverrides }} {{ modalClass }}\"\n  >\n    <div *ngIf=\"closer\" class=\"e11-absolute e11--right-3.5 e11--top-3.5 e11-z-10\">\n      <e11-closer [size]=\"'lg'\" [darkMode]=\"darkMode\" [classOverrides]=\"'e11-border'\" (close)=\"close()\"></e11-closer>\n    </div>\n    <div\n      (closeOnClickOutside)=\"closeFromClickOutside()\"\n      [ngClass]=\"{\n        'e11-bg-skin-app-bg-dark e11-border-skin-black': darkMode,\n        'e11-bg-skin-white e11-border-skin-app-borders': !darkMode,\n        'e11-flex e11-flex-col': width === 'full',\n        'e11-rounded': width !== 'full'\n      }\"\n      class=\"e11-modal-content e11-relative e11-overflow-hidden e11-border {{ contentClassOverrides }} {{ contentClass }}\"\n    >\n      <div\n        *ngIf=\"showHeader\"\n        [ngClass]=\"darkMode ? 'e11-bg-skin-app-bg-dark' : 'e11-border-b e11-border-skin-app-borders'\"\n        class=\"e11-modal-header e11-flex e11-w-full e11-items-center e11-px-4 e11-modal-{{ titleAlign }} {{ headerClassOverrides }} {{ headerClass }}\"\n      >\n        <div class=\"e11-grow e11-line-clamp-1 e11-pr-5\">\n          <h3 *ngIf=\"title\" [ngClass]=\"darkMode ? 'e11-text-skin-extra-light' : ''\">{{ title }}</h3>\n          <ng-content *ngIf=\"e11TemplateMap[ModalTemplateKey.Header]\" select=\"[modal-header]\"></ng-content>\n          <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Header]\"></ng-container>\n        </div>\n        <div class=\"cta e11-grow e11-flex e11-items-center e11-justify-end\">\n          <span\n            (click)=\"handleHeaderCTAClick(cta)\"\n            *ngFor=\"let cta of headerCTAs\"\n            class=\"e11-border e11-border-skin-app-borders e11-mr-2 e11-px-4 e11-py-1 e11-rounded e11-cursor-pointer e11-h-8 hover:e11-text-skin-primary-accent hover:e11-border-skin-primary-accent hover:e11-shadow\"\n            >{{ cta }}</span\n          >\n          <e11-closer *ngIf=\"!hideCloseButton\" [size]=\"'lg'\" [darkMode]=\"darkMode\" [classOverrides]=\"''\" (close)=\"close()\"></e11-closer>\n        </div>\n      </div>\n\n      <div\n        [ngClass]=\"{\n          'e11-text-skin-extra-light': darkMode,\n          'e11-flex-grow': width === 'full'\n        }\"\n        class=\"e11-modal-body e11-p-4 e11-modal-responsive e11-modal-{{ bodyHeight }} e11-overflow-y-auto {{ bodyClassOverrides }} {{ bodyClass }}\"\n      >\n        <ng-content *ngIf=\"!e11TemplateMap[ModalTemplateKey.Body]\" select=\"[modal-body]\"></ng-content>\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Body]\"></ng-container>\n      </div>\n\n      <div\n        *ngIf=\"showFooter\"\n        [ngClass]=\"darkMode ? 'e11-bg-skin-app-bg-dark e11-text-skin-extra-light' : 'e11-bg-skin-grey/20'\"\n        class=\"e11-modal-footer e11-p-4 {{ footerClassOverrides }} {{ footerClass }}\"\n      >\n        <ng-content *ngIf=\"!e11TemplateMap[ModalTemplateKey.Footer]\" select=\"[modal-footer]\"></ng-content>\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Footer]\"></ng-container>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"hasBackdrop && isBackdropOpen\"\n  [@animationFadeInOut]\n  [ngClass]=\"{ 'e11-bg-black/70': backdropStyle === 'dark', 'e11-bg-white/70': backdropStyle === 'light' }\"\n  class=\"e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm\"\n></div>\n", styles: [".e11-modal-container{position:fixed;inset:0;width:100%;height:100%;display:flex;justify-content:center;z-index:-1}.e11-modal-container .e11-modal.e11-modal-full{width:calc(100% - 3rem);min-width:calc(100% - 3rem);max-width:calc(100% - 3rem)}.e11-modal-container.e11-modal-container-active{z-index:51}.e11-modal-container .e11-modal-content .e11-modal-header{height:60px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: E11CloserModule }, { kind: "component", type: i2.E11CloserComponent, selector: "e11-closer", inputs: ["classOverrides", "icon", "size", "darkMode"], outputs: ["close"] }], animations: [animationFadeInOut(), E11Animations.fadeInUp, E11Animations.scaleUp, ...E11Animations.scaleSlideUp], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-modal', standalone: true, imports: [CommonModule, E11CloserModule], animations: [animationFadeInOut(), E11Animations.fadeInUp, E11Animations.scaleUp, ...E11Animations.scaleSlideUp], encapsulation: ViewEncapsulation.None, template: "<div\n  *ngIf=\"isOpened\"\n  [@animationScaleSlideUp]\n  [ngClass]=\"{ 'e11-modal-container-active': isOpened, 'e11-overflow-y-scroll': bodyHeight === 'auto' }\"\n  class=\"e11-modal-container\"\n>\n  <div\n    [ngClass]=\"{ 'e11-flex e11-min-h-screen e11-mt-0': width === 'full', 'e11-mt-16': width !== 'full' }\"\n    class=\"e11-modal e11-absolute e11-modal-responsive e11-modal-{{ width }} {{ modalClassOverrides }} {{ modalClass }}\"\n  >\n    <div *ngIf=\"closer\" class=\"e11-absolute e11--right-3.5 e11--top-3.5 e11-z-10\">\n      <e11-closer [size]=\"'lg'\" [darkMode]=\"darkMode\" [classOverrides]=\"'e11-border'\" (close)=\"close()\"></e11-closer>\n    </div>\n    <div\n      (closeOnClickOutside)=\"closeFromClickOutside()\"\n      [ngClass]=\"{\n        'e11-bg-skin-app-bg-dark e11-border-skin-black': darkMode,\n        'e11-bg-skin-white e11-border-skin-app-borders': !darkMode,\n        'e11-flex e11-flex-col': width === 'full',\n        'e11-rounded': width !== 'full'\n      }\"\n      class=\"e11-modal-content e11-relative e11-overflow-hidden e11-border {{ contentClassOverrides }} {{ contentClass }}\"\n    >\n      <div\n        *ngIf=\"showHeader\"\n        [ngClass]=\"darkMode ? 'e11-bg-skin-app-bg-dark' : 'e11-border-b e11-border-skin-app-borders'\"\n        class=\"e11-modal-header e11-flex e11-w-full e11-items-center e11-px-4 e11-modal-{{ titleAlign }} {{ headerClassOverrides }} {{ headerClass }}\"\n      >\n        <div class=\"e11-grow e11-line-clamp-1 e11-pr-5\">\n          <h3 *ngIf=\"title\" [ngClass]=\"darkMode ? 'e11-text-skin-extra-light' : ''\">{{ title }}</h3>\n          <ng-content *ngIf=\"e11TemplateMap[ModalTemplateKey.Header]\" select=\"[modal-header]\"></ng-content>\n          <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Header]\"></ng-container>\n        </div>\n        <div class=\"cta e11-grow e11-flex e11-items-center e11-justify-end\">\n          <span\n            (click)=\"handleHeaderCTAClick(cta)\"\n            *ngFor=\"let cta of headerCTAs\"\n            class=\"e11-border e11-border-skin-app-borders e11-mr-2 e11-px-4 e11-py-1 e11-rounded e11-cursor-pointer e11-h-8 hover:e11-text-skin-primary-accent hover:e11-border-skin-primary-accent hover:e11-shadow\"\n            >{{ cta }}</span\n          >\n          <e11-closer *ngIf=\"!hideCloseButton\" [size]=\"'lg'\" [darkMode]=\"darkMode\" [classOverrides]=\"''\" (close)=\"close()\"></e11-closer>\n        </div>\n      </div>\n\n      <div\n        [ngClass]=\"{\n          'e11-text-skin-extra-light': darkMode,\n          'e11-flex-grow': width === 'full'\n        }\"\n        class=\"e11-modal-body e11-p-4 e11-modal-responsive e11-modal-{{ bodyHeight }} e11-overflow-y-auto {{ bodyClassOverrides }} {{ bodyClass }}\"\n      >\n        <ng-content *ngIf=\"!e11TemplateMap[ModalTemplateKey.Body]\" select=\"[modal-body]\"></ng-content>\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Body]\"></ng-container>\n      </div>\n\n      <div\n        *ngIf=\"showFooter\"\n        [ngClass]=\"darkMode ? 'e11-bg-skin-app-bg-dark e11-text-skin-extra-light' : 'e11-bg-skin-grey/20'\"\n        class=\"e11-modal-footer e11-p-4 {{ footerClassOverrides }} {{ footerClass }}\"\n      >\n        <ng-content *ngIf=\"!e11TemplateMap[ModalTemplateKey.Footer]\" select=\"[modal-footer]\"></ng-content>\n        <ng-container *ngTemplateOutlet=\"e11TemplateMap[ModalTemplateKey.Footer]\"></ng-container>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"hasBackdrop && isBackdropOpen\"\n  [@animationFadeInOut]\n  [ngClass]=\"{ 'e11-bg-black/70': backdropStyle === 'dark', 'e11-bg-white/70': backdropStyle === 'light' }\"\n  class=\"e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm\"\n></div>\n", styles: [".e11-modal-container{position:fixed;inset:0;width:100%;height:100%;display:flex;justify-content:center;z-index:-1}.e11-modal-container .e11-modal.e11-modal-full{width:calc(100% - 3rem);min-width:calc(100% - 3rem);max-width:calc(100% - 3rem)}.e11-modal-container.e11-modal-container-active{z-index:51}.e11-modal-container .e11-modal-content .e11-modal-header{height:60px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { title: [{
                type: Input
            }], width: [{
                type: Input
            }], bodyHeight: [{
                type: Input
            }], titleAlign: [{
                type: Input
            }], hideCloseButton: [{
                type: Input
            }], closer: [{
                type: Input
            }], bodyClassOverrides: [{
                type: Input
            }], bodyClass: [{
                type: Input
            }], modalClassOverrides: [{
                type: Input
            }], modalClass: [{
                type: Input
            }], contentClassOverrides: [{
                type: Input
            }], contentClass: [{
                type: Input
            }], headerClassOverrides: [{
                type: Input
            }], headerClass: [{
                type: Input
            }], footerClassOverrides: [{
                type: Input
            }], footerClass: [{
                type: Input
            }], showFooter: [{
                type: Input
            }], showHeader: [{
                type: Input
            }], darkMode: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], headerCTAs: [{
                type: Input
            }], hasBackdrop: [{
                type: Input
            }], backdropStyle: [{
                type: Input
            }], modalOpened: [{
                type: Output
            }], onModalOpened: [{
                type: Output
            }], modalClosed: [{
                type: Output
            }], onModalClosed: [{
                type: Output
            }], headerCTAClicked: [{
                type: Output
            }], onHeaderCTAClicked: [{
                type: Output
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class E11ModalGlobalService {
    constructor() {
        /**
         * Observable that holds the current configuration of the global modal.
         * It emits `null` when the modal is closed, and an instance of `E11GlobalModalConfig` when opened.
         */
        this.config$ = new BehaviorSubject(null);
        /**
         * Observable that emits the type of modal when it is opened.
         * emits type that's passed into openModal
         */
        this.modalOpened$ = new Subject();
        /**
         * Observable that emits the type of modal when it is closed.
         * emits type that was passed into openModal
         */
        this.modalClosed$ = new Subject();
        /**
         * Observable that emits when a Call-To-Action (CTA) button in the modal header is clicked.
         */
        this.ctaClicked$ = new Subject();
    }
    /**
     * Opens the global modal with the specified configuration.
     * Type is used by subscribers of @modalOpened$ and @modalClosed$ to know which modal was opened/closed
     * The `config` parameter allows partial customization of the modal, including its content and behavior.
     * It also notifies subscribers via the @modalOpened$ observable.
     *
     * @param config - The configuration object for the modal, which can include a title, body, footer, and more.
     */
    openModal(config) {
        const showFooter = !!config.footer || config.showFooter || false; // Rip out the footer if the template isn't defined
        const configToUse = { ...defaultModalConfig, showFooter, ...config };
        this.config$.next(configToUse);
        this.currentModal = config.type;
        this.modalOpened$.next(config.type);
    }
    /**
     * Closes the currently active modal.
     * Resets the modal configuration to `null` and emits the modal type to subscribers via the `modalClosed$` observable.
     */
    closeModal() {
        this.config$.next(null);
        this.modalClosed$.next(this.currentModal);
        this.currentModal = undefined;
        this.ctaClicked$.next(undefined);
    }
    /**
     * Emits a header Call-To-Action (CTA) click event.
     * The `ctaClicked$` observable is used by subscribers to respond to the specific action.
     *
     * @param cta - The string identifier of the CTA action that was clicked.
     *
     * TODO: consider generic typing
     * - Because the cta text is used by both the view and the @ctaClicked$ observable, if you use the same text (e.g. View Detail) and are subscribed in two places, both subscriptions might fire
     * TODO: Consider passing {type: string, cta: string} instead of just cta
     */
    headerCTAClicked(cta) {
        this.ctaClicked$.next(cta);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalGlobalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalGlobalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalGlobalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * A global modal component that displays dynamic content in a modal format and can be triggered from anywhere in the application.
 *
 * This component listens for configuration updates from the `E11ModalGlobalService` and dynamically opens or closes the modal
 * based on the provided configuration.
 *
 * This modal provides several benefits:
 * - You can always trigger it regardless of whether the original component is destroyed
 * - It only requires one modal in the DOM - no filling the DOM with unnecessary modal code
 * - It can be triggered from any part of the application.
 *
 * @remarks
 * - The component is initialized with default modal configurations and can be customized via the `E11GlobalModalConfig`.
 * - Uses Angular animations (`animationFadeInOut`) for fade-in and fade-out effects.
 * - Handles the modal's header, body, and footer content dynamically using Angular's `ngTemplateOutlet`.
 *
 * @example
 * <e11-modal-global></e11-modal-global>
 *
 * @example
 * // Trigger the modal from anywhere using the modal service
 * this.modalService.openModal({
 *   title: 'Modal Title',
 *   body: 'Content for the body',
 *   footer: 'Footer content',
 * });
 */
class E11ModalGlobalComponent {
    constructor(modalService) {
        this.modalService = modalService;
        this.defaultModalConfig = defaultModalConfig;
        this.config = null;
        this.destroy$ = new Subject();
    }
    /**
     * Subscribes to the modal configuration
     * and opens or closes the modal based on the configuration changes.
     */
    ngAfterViewInit() {
        this.modalService.config$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
            this.config = config;
            // setTimeout is to ensure e11-modal is in the DOM when open() is called
            if (config)
                setTimeout(() => this.globalModal.open());
            else
                setTimeout(() => this.globalModal?.close());
        });
    }
    /**
     * Cleans up subscriptions
     */
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    /**
     * Handles click events on header Call-To-Action (CTA) buttons and passes the event to the modal service.
     * @param cta - The identifier of the CTA button that was clicked.
     */
    headerCTAClicked(cta) {
        this.modalService.headerCTAClicked(cta);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalGlobalComponent, deps: [{ token: E11ModalGlobalService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ModalGlobalComponent, isStandalone: true, selector: "e11-modal-global", viewQueries: [{ propertyName: "globalModal", first: true, predicate: ["globalModal"], descendants: true }], ngImport: i0, template: `
    <e11-modal
      #globalModal
      *ngIf="config"
      [title]="config.title"
      [width]="config.width"
      [bodyHeight]="config.bodyHeight"
      [titleAlign]="config.titleAlign"
      [hideCloseButton]="config.hideCloseButton"
      [closer]="config.closer"
      [darkMode]="config.darkMode"
      [showHeader]="config.showHeader"
      [showFooter]="config.showFooter"
      [hasBackdrop]="config.hasBackdrop"
      [headerCTAs]="config.headerCTAs ?? []"
      [backdropStyle]="config.backdropStyle"
      [closeOnClickOutside]="config.closeOnClickOutside"
      [closeOnEscape]="config.closeOnEscape"
      (modalClosed)="modalService.closeModal()"
      (headerCTAClicked)="headerCTAClicked($event)"
    >
      <ng-container modal-header *ngIf="config?.header">
        <ng-container *ngTemplateOutlet="config.header!"></ng-container>
      </ng-container>

      <ng-container modal-body *ngIf="config?.body">
        <ng-container *ngTemplateOutlet="config.body!"></ng-container>
      </ng-container>

      <ng-container modal-footer *ngIf="config?.footer">
        <ng-container *ngTemplateOutlet="config.footer!"></ng-container>
      </ng-container>
    </e11-modal>
  `, isInline: true, dependencies: [{ kind: "component", type: E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalGlobalComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-modal-global',
                    standalone: true,
                    imports: [E11ModalComponent, CommonModule],
                    encapsulation: ViewEncapsulation.None,
                    template: `
    <e11-modal
      #globalModal
      *ngIf="config"
      [title]="config.title"
      [width]="config.width"
      [bodyHeight]="config.bodyHeight"
      [titleAlign]="config.titleAlign"
      [hideCloseButton]="config.hideCloseButton"
      [closer]="config.closer"
      [darkMode]="config.darkMode"
      [showHeader]="config.showHeader"
      [showFooter]="config.showFooter"
      [hasBackdrop]="config.hasBackdrop"
      [headerCTAs]="config.headerCTAs ?? []"
      [backdropStyle]="config.backdropStyle"
      [closeOnClickOutside]="config.closeOnClickOutside"
      [closeOnEscape]="config.closeOnEscape"
      (modalClosed)="modalService.closeModal()"
      (headerCTAClicked)="headerCTAClicked($event)"
    >
      <ng-container modal-header *ngIf="config?.header">
        <ng-container *ngTemplateOutlet="config.header!"></ng-container>
      </ng-container>

      <ng-container modal-body *ngIf="config?.body">
        <ng-container *ngTemplateOutlet="config.body!"></ng-container>
      </ng-container>

      <ng-container modal-footer *ngIf="config?.footer">
        <ng-container *ngTemplateOutlet="config.footer!"></ng-container>
      </ng-container>
    </e11-modal>
  `,
                }]
        }], ctorParameters: () => [{ type: E11ModalGlobalService }], propDecorators: { globalModal: [{
                type: ViewChild,
                args: ['globalModal']
            }] } });

class E11ModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ModalModule, imports: [CommonModule, E11ButtonModule, E11DirectivesModule, E11UtilModule, E11CloserModule, E11ModalComponent, E11ModalGlobalComponent], exports: [E11ModalComponent, E11ModalGlobalComponent, E11UtilModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalModule, imports: [CommonModule, E11ButtonModule, E11DirectivesModule, E11UtilModule, E11CloserModule, E11ModalComponent, E11ModalGlobalComponent, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, E11ButtonModule, E11DirectivesModule, E11UtilModule, E11CloserModule, E11ModalComponent, E11ModalGlobalComponent],
                    exports: [E11ModalComponent, E11ModalGlobalComponent, E11UtilModule],
                    providers: [], // Be extremely careful about declaring services in providers arrays - it can prevent them from being singletons
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ModalComponent, E11ModalGlobalComponent, E11ModalGlobalService, E11ModalModule, ModalTemplateKey };
